/* eslint-disable no-underscore-dangle */
/**
 * `/portal/trip/:bookingId` — everything a driver needs for one job.
 *
 * Every trip opens with the route map (PortalRouteMap: OpenStreetMap tiles,
 * OSRM driving route, through the stops for a programme). Below it, two
 * shapes share this screen:
 *   - a programme (`programme.stops` present): a timeline of stops and the
 *     event it belongs to;
 *   - a plain transfer: pickup → dropoff and the passenger.
 *
 * The foot carries the live progress strip: each tap posts the next driver
 * event to `/portal/trip-event` and the screen re-renders from the trip the
 * API sends back. Once the driver has arrived at the pickup — and again while
 * the guest is in the car ("Várakozás a célnál") — the waiting / overtime
 * panel appears above it. Right after "Letettem" the driver is asked "Volt
 * túlóra?", and the dropped-off trip keeps a "Túlóra jelentése" button for
 * minutes remembered later — until the trip is Completed. Whatever overtime
 * was recorded is listed on the screen (OvertimeCard) — minutes only. A named passenger list
 * (`passenger_manifest`) is shown whenever the booking carries one.
 *
 * The office may mark the trip Completed before the driver has tapped
 * "Letettem" — routine, and from then on the API refuses every step. Such a
 * trip shows as closed by the office: no step button, the strip keeps what
 * was recorded, and nothing overtime-related is offered — a closed trip
 * takes no more driver input, and the API refuses a report or a new timer on
 * it (a timer already running can still be stopped, in the waiting panel).
 * The same holds once the driver's own "Letettem" trip is Completed: the
 * driver reports before the trip closes. A step refused because the office
 * closed the trip while
 * this screen was open says so and re-reads the trip; so does bringing the
 * app back to the foreground, so a trip closed while the phone was locked
 * never sits there offering a step.
 *
 * "Nem jelent meg" is not a confirmation any more, it is the office's
 * procedure: NoShowSheet gates the report on the 60 minutes since the
 * flight ACTUALLY LANDED (or, with no landing to read and away from an
 * airport, on the office having agreed it), on the checkpoints, on a note,
 * and on a call THIS APP recorded. Which is why the customer's Hívás
 * button now posts `passenger_call_attempted`: it used to be a bare `tel:`
 * link that left no trace, so the old sheet could only ever record that
 * the driver had ticked "I called". While the driver is waiting at an
 * airport pickup they are asked "Névtáblával várakozol?" — the photo that
 * answers it is the same evidence, taken at the only moment it can be, and
 * is offered to the sheet if a no-show is ever filed.
 *
 * Navigáció (the buttons and a tap on the map) goes where the driver is
 * heading: the pickup until "Utas a kocsiban", then the drop-off, or on a
 * programme the next stop not yet left (portalFormat's navigationTarget).
 * "Nem jelent meg" (waiting at the pickup) always asks before it sends.
 *
 * A trip with a flight carries the flight's live state too (FlightCard: how
 * late the plane is, when it is now expected), and such a trip re-reads
 * itself every five minutes while it is still going ahead — a delay is the
 * one thing on this screen that changes on its own, and it moves the free
 * waiting clock with it. It also re-reads whenever the app comes back to the
 * foreground. Both triggers live in `useRefetchWhileLive`; a read that fails
 * leaves the trip on screen untouched, and once what is on screen is older
 * than the API's own live window the flight card says so rather than let a
 * confident countdown run off an arrival nobody could confirm.
 *
 * The customer's phone (and the WhatsApp / call buttons) exists only inside
 * the API's 24-hours-either-side-of-pickup window. Before it, the card says
 * when the number will appear, and the screen re-reads the trip once at that
 * moment — router state from the day list may be hours old.
 *
 * There is deliberately no price, payment or margin anywhere on this page. The
 * `/portal/trips` payload carries none, and nothing here invents one. (The
 * expense sheet's amount is the driver's own claim, typed in by them.)
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useBackTo from '../../Hooks/useBackTo';
import { fetchPortalTrips, getPortalPrincipal, sendPortalTripEvent } from '../../Services/PortalApi';
import { formatTime } from '../../Utils/timezone';
import { navigationUrl } from '../../Utils/geoPoint';
import {
  ADVANCE_LABELS,
  brandingAllowed,
  budapestDayWindow,
  canReportOvertime,
  dayLabel,
  flightDataStale,
  isDroppedOff,
  isInCar,
  isAirportPickupTrip,
  isJourneyUnderWay,
  isNoShow,
  isOvertimeAlreadyStopped,
  isTripClosed,
  isTripClosedRefusal,
  longDate,
  formatAmount,
  LIVE_LOCATION_COPY,
  navigationTarget,
  nextTripEvent,
  noShowEvidenceBody,
  OFFICE_WHATSAPP_HINT,
  passengerMessage,
  passengerManifest,
  passengerSummary,
  phoneReleaseAt,
  isWaitingAtPickup,
  LIVE_FLIGHT_TTL_MS,
  place,
  pollsFlight,
  principalName,
  portalErrorMessage,
  portalLoadErrorMessage,
  programmeStops,
  progressIndex,
  statusLabel,
  totalPassengers,
  vehicleLabel,
} from './portalFormat';
import {
  CustomerCard, Facts, Manifest, Note, RouteOrProgramme,
} from './Components/TripCards';
import PortalRouteMap from './Components/PortalRouteMap';
import ProgressStrip from './Components/ProgressStrip';
import FlightCard from './Components/FlightCard';
import WaitingPanel from './Components/WaitingPanel';
import OvertimeCard from './Components/OvertimeCard';
import OvertimeReportSheet from './Components/OvertimeReportSheet';
import RecordExpense from './Components/RecordExpense';
import NoShowSheet from './Components/NoShowSheet';
import NameSignAsk from './Components/NameSignAsk';
import Toast from './Components/Toast';
import NameSign from './NameSign';
import currentPosition from './currentPosition';
import useLiveLocation from './useLiveLocation';
import useNow from './useNow';
import useRefetchWhileLive from './useRefetchWhileLive';
import { ChevronLeftIcon, NameSignIcon, NavigateIcon } from './Components/Icons';

// A deep link (or a reload on the trip screen) has no router state to lean on,
// so the trip is found in a window around today.
const LOOKBACK_DAYS = 1;
const LOOKAHEAD_DAYS = 30;

// setTimeout's ceiling (~24.8 days). No trip screen sits open that long
// waiting for a phone number to be released.
const MAX_TIMER_MS = 2 ** 31 - 1;

// On a programme with the guest aboard, the next stop is judged by the clock:
// re-read it this often so Navigáció moves on without a tap.
const TARGET_CLOCK_MS = 60 * 1000;

const findTrip = async (bookingId) => {
  const rows = await fetchPortalTrips(budapestDayWindow(LOOKBACK_DAYS, LOOKAHEAD_DAYS));
  return rows.find((row) => row._id === bookingId) || null;
};

function TripActions({ href, onNameSign }) {
  return (
    <div className="pp-actions">
      {href ? (
        <a className="pp-btn pp-btn--outline pp-btn--grow" href={href} target="_blank" rel="noreferrer">
          <NavigateIcon size={18} />
          Navigáció
        </a>
      ) : null}
      <button type="button" className="pp-btn pp-btn--dark pp-btn--grow" onClick={onNameSign}>
        <NameSignIcon size={18} />
        Névtábla
      </button>
    </div>
  );
}

export default function PortalTripDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * Back to the day as it was left — the day the week strip was showing
   * (`?nap=` in the URL behind this screen) and the trip that was being
   * read (its scroll, in ScrollMemory). Only a POP restores either, and
   * `navigate(-1)` alone was a dead button on a screen reached cold: a
   * reload, or a link opened straight into one job. See `Hooks/useBackTo`.
   */
  const backToDay = useBackTo('/portal');
  const [trip, setTrip] = useState(() => location.state?.trip || null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!location.state?.trip);
  const [signOpen, setSignOpen] = useState(false);
  const [toast, setToast] = useState('');
  // Which event is on the wire ('' when idle). Drives the disabled state.
  const [busy, setBusy] = useState('');
  const [eventError, setEventError] = useState('');
  // The overtime report sheet: '' closed, 'prompt' — asked right after
  // "Letettem" ("Volt túlóra?") — or 'later', from "Túlóra jelentése".
  const [reportSheet, setReportSheet] = useState('');
  // The no-show procedure, opened from the waiting panel.
  const [noShowOpen, setNoShowOpen] = useState(false);
  // The fix the report will carry, asked for when the sheet opens rather
  // than at submit: the sheet has to be able to SAY whether the position
  // will corroborate the claim, and it cannot say that about a fix it has
  // not got yet. `undefined` while the phone is thinking, `null` when it
  // gave nothing, `{ lat, lon }` when it did.
  const [noShowFix, setNoShowFix] = useState(undefined);
  // A photo of the name sign, taken while waiting (NameSignAsk). Held here
  // rather than in the sheet because it is taken long before the sheet is
  // opened — if it ever is.
  const [signPhoto, setSignPhoto] = useState(null);

  // The real double-tap guard. State alone is not enough: two taps in the
  // same frame both read `busy === ''` before React re-renders.
  const inFlight = useRef(false);
  const mounted = useRef(true);
  // Counts the taps the API has answered. A re-read that started before one
  // of them is older than what the screen shows, and is dropped.
  const answered = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (trip) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const found = await findTrip(bookingId);
        if (cancelled) return;
        if (found) setTrip(found);
        else setError('Ez a fuvar nem található a beosztásodban.');
      } catch (failure) {
        if (!cancelled) {
          setError(portalLoadErrorMessage(
            failure,
            'Nem sikerült betölteni a fuvart. Ellenőrizd a netkapcsolatot.',
          ));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [bookingId, trip]);

  const closeSign = useCallback(() => setSignOpen(false), []);
  const clearToast = useCallback(() => setToast(''), []);

  // Shows the trip the API last returned, and keeps the router's copy current
  // too: a reload re-reads history state, and would otherwise show the step
  // before this tap.
  const showTrip = useCallback((updated) => {
    const merged = { ...trip, ...updated };
    setTrip(merged);
    navigate(location.pathname, { replace: true, state: { ...location.state, trip: merged } });
  }, [location, navigate, trip]);

  /**
   * When the API last confirmed this trip for us, and when a refresh last
   * failed. The pair is the only honest answer to "is the arrival on screen
   * still worth believing?" — see `flightDataStale`.
   *
   * `okAt` starts at mount because that is when the data arrived: either
   * this screen has just fetched the trip itself, or it came straight from
   * a day list that had. Each failure stores its own timestamp rather than
   * a flag, so a second failed beat is a real state change and the screen
   * re-renders to admit it.
   */
  const [refresh, setRefresh] = useState(() => ({ okAt: Date.now(), failedAt: 0 }));

  // Re-reads the trip and shows it — unless the driver has left the screen,
  // or a tap was answered meanwhile (its answer is the newer one). A failure
  // is quiet: the trip on screen stays exactly as it is, because a driver at
  // the barrier is better served by a slightly old arrival than by a blank
  // panel, and the next beat or tap brings it up to date.
  const rereadTrip = useCallback(async () => {
    const answeredBefore = answered.current;
    try {
      const fresh = await findTrip(bookingId);
      if (!mounted.current) return;
      setRefresh({ okAt: Date.now(), failedAt: 0 });
      if (fresh && answered.current === answeredBefore) showTrip(fresh);
    } catch {
      if (mounted.current) setRefresh((prev) => ({ ...prev, failedAt: Date.now() }));
    }
  }, [bookingId, showTrip]);

  // The customer's number is withheld until 24h before pickup. Once that
  // moment has passed — already, or while this screen is open — re-read the
  // trip ONCE so the number appears without a manual reload. One timer, set
  // for that moment; never a polling loop.
  const phoneDueAt = phoneReleaseAt(trip);
  const phoneReread = useRef('');
  useEffect(() => {
    if (!phoneDueAt || phoneReread.current === phoneDueAt) return undefined;
    const delay = new Date(phoneDueAt).valueOf() - Date.now();
    if (!Number.isFinite(delay) || delay > MAX_TIMER_MS) return undefined;

    const timer = setTimeout(() => {
      phoneReread.current = phoneDueAt;
      rereadTrip();
    }, Math.max(0, delay));
    return () => clearTimeout(timer);
  }, [phoneDueAt, rereadTrip]);

  // Not while a tap is on the wire: that tap's own answer is the newer trip,
  // and a read started now would race it. The next beat picks the flight up.
  const refreshTrip = useCallback(() => {
    if (inFlight.current) return undefined;
    return rereadTrip();
  }, [rereadTrip]);

  /**
   * Keeping this screen current, both triggers in one place
   * (`useRefetchWhileLive`):
   *
   * - back in the foreground, on ANY loaded trip — the phone may have sat in
   *   a pocket for hours while the office closed or changed the trip, the
   *   same visibilitychange PortalRoute renews the session on;
   * - on a beat, but only while there is something that moves on its own,
   *   which on this screen means the flight and nothing else. `pollsFlight`
   *   is deliberately narrow: a trip with a flight, still going ahead, and
   *   near enough its pickup that the API would look the flight up at all.
   *   A day's work with no imminent arrival polls nothing.
   *
   * The beat is the API's own live cache TTL, so however many drivers have
   * the trip open it costs at most one upstream lookup per flight per
   * window — asking faster would re-serve the same answer.
   */
  const loaded = Boolean(trip);
  const polls = loaded && pollsFlight(trip);
  useRefetchWhileLive(refreshTrip, {
    enabled: loaded,
    live: polls,
    intervalMs: LIVE_FLIGHT_TTL_MS,
  });

  // Sends one trip event — `extra` carries the overtime fields (`stage`,
  // `minutes`, `note`). Resolves to true once the API has taken it. Every tap
  // on this screen goes through here, so one lock covers them all.
  const sendEvent = useCallback(async (event, knownPosition, extra = {}) => {
    if (!event || !trip || inFlight.current) return false;
    inFlight.current = true;
    setBusy(event);
    setEventError('');
    try {
      // Location is best-effort: a "no" to the permission prompt, or no fix,
      // still sends the event — just without coordinates. A minutes report
      // can come hours later, so where the phone is then means nothing.
      //
      // `knownPosition` is the no-show's: the sheet already asked for a fix
      // so it could tell the driver whether the report would be
      // corroborated, and asking twice could send a different answer than
      // the one on screen.
      let position = knownPosition;
      if (position === undefined) {
        position = event === 'overtime_reported' ? null : await currentPosition();
      }
      const updated = await sendPortalTripEvent({
        booking_id: trip._id,
        event,
        ...extra,
        ...(position ? { lat: position.lat, lon: position.lon } : {}),
      });
      // The driver may have backed out while this was on the wire.
      if (!mounted.current) return false;
      answered.current += 1;
      // Dropped off (3 → 4): the drop-off timer was closed server-side; ask
      // whether there was overtime the timers did not catch.
      if (event === 'arrived_at_dropoff') setReportSheet('prompt');
      showTrip(updated);
      return true;
    } catch (failure) {
      if (!mounted.current) return false;
      // "Túlóra vége" on a timer that is no longer running: it is stopped,
      // which is what the driver wanted. Show the trip as it now stands.
      if (event === 'overtime_ended' && isOvertimeAlreadyStopped(failure)) {
        await rereadTrip();
        return true;
      }
      // The office closed the trip while this screen was open: say so (the
      // shared error copy has the sentence) and re-read the trip, which then
      // shows closed — no step to offer, nothing to retry.
      if (isTripClosedRefusal(failure)) {
        setNoShowOpen(false);
        setEventError(portalErrorMessage(failure));
        await rereadTrip();
        return false;
      }
      setEventError(portalErrorMessage(failure, {
        // The API refuses a step it can't record (outside the trip's time
        // window, trip no longer active) with 400 — retrying won't help.
        400: `Ezt a lépést most nem lehet rögzíteni. ${OFFICE_WHATSAPP_HINT}`,
        404: `Ez a fuvar már nem szerepel a beosztásodban. ${OFFICE_WHATSAPP_HINT}`,
        network: 'Nem ment át — nincs kapcsolat. Próbáld újra, amint van térerő.',
      }));
      return false;
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy('');
    }
  }, [rereadTrip, showTrip, trip]);

  const closeReport = useCallback(() => {
    if (inFlight.current) return;
    setReportSheet('');
    setEventError('');
  }, []);

  const openReport = useCallback(() => {
    setEventError('');
    setReportSheet('later');
  }, []);

  const submitReport = useCallback(async ({ minutes, note, stage }) => {
    const sent = await sendEvent('overtime_reported', undefined, { minutes, note, stage });
    if (sent && mounted.current) {
      setReportSheet('');
      setToast('Túlóra rögzítve.');
    }
  }, [sendEvent]);

  const openNoShow = useCallback(() => {
    setEventError('');
    setNoShowFix(undefined);
    setNoShowOpen(true);
    // Ask the phone now, so the sheet can be honest about corroboration
    // before the driver commits to anything. Never blocks the sheet.
    currentPosition().then((point) => {
      if (mounted.current) setNoShowFix(point);
    });
  }, []);

  const closeNoShow = useCallback(() => {
    if (inFlight.current) return;
    setNoShowOpen(false);
    setEventError('');
  }, []);

  const confirmNoShow = useCallback(async (evidence) => {
    const sent = await sendEvent(
      'no_show',
      // A fix still being resolved counts as none: the sheet said as much.
      noShowFix ?? null,
      { no_show_evidence: noShowEvidenceBody(trip, evidence) },
    );
    if (sent && mounted.current) {
      setNoShowOpen(false);
      setToast('Rögzítve: az utas nem jelent meg.');
    }
  }, [noShowFix, sendEvent, trip]);

  /**
   * "Névtáblával várakozol?" — recorded THE MOMENT it is answered, not
   * carried to a no-show. Held in component state it would be lost to a
   * reload (this is a PWA and iOS evicts backgrounded tabs), and a trip
   * that ended normally would keep no trace that the driver stood there
   * with a sign — which is exactly what gets disputed later. The answer
   * comes back on the trip (`name_sign`), so the sheet and the passenger's
   * WhatsApp read the server's copy rather than this screen's memory.
   */
  const answerNameSign = useCallback((answer) => {
    sendEvent('name_sign_answered', undefined, { name_sign: answer });
  }, [sendEvent]);

  /**
   * The driver tapped Hívás or WhatsApp on the customer. Fired alongside
   * the link, never instead of it — the dialer (or WhatsApp) must open
   * whatever we do, and BOTH count towards step 3: the instruction names
   * the call button but asks for a recorded attempt to REACH the
   * passenger, and at an airport a message is often the better one. The
   * POST is not awaited (the phone is already switching apps) and its
   * failure is quiet: coming back to the app re-reads the trip, so the
   * attempt shows up if it landed, and the driver can simply ring again if
   * it did not. Nothing here blocks the call itself.
   */
  const recordContact = useCallback((method) => {
    if (inFlight.current) return;
    sendEvent('passenger_contact_attempted', undefined, { contact_method: method });
  }, [sendEvent]);

  // Two things on this screen change with nothing but time, and both are a
  // minute's resolution: which stop Navigáció heads for on a programme with
  // the guest aboard, and which message the passenger's WhatsApp opens with
  // (en route becomes "running late" at the pickup time; waiting becomes
  // "I cannot find you" when the free waiting runs out). Everything else
  // heads for a fixed place or a fixed sentence, and must not start a clock
  // that can never change the answer.
  const clockMatters = (programmeStops(trip).length > 0 && isInCar(trip))
    || isWaitingAtPickup(trip)
    || progressIndex(trip) === 1;
  const now = useNow(clockMatters, TARGET_CLOCK_MS);

  /**
   * The driver's position, reported to the booking's partner while the
   * journey is under way — Booking.com show the passenger where the car is.
   *
   * ONE CONDITION: the journey is under way (`isJourneyUnderWay`: between
   * "Úton vagyok" and "Letettem", on a trip the office has not closed — the
   * same `driver_status.state` the progress strip reads). There is no switch
   * and no stored preference: this is on for every driver, by the office's
   * decision, and the line below is how a driver is told so rather than
   * discovering it later.
   *
   * It is a WEB app, so this follows the screen: it reports while the trip is
   * open and the phone awake and stops when the driver switches away or locks
   * it. There is no background version to add — see useLiveLocation. The
   * driver's own location permission is still the real gate, and a denial
   * stops the loop rather than being asked again.
   */
  const sharingLocation = Boolean(trip) && isJourneyUnderWay(trip);
  useLiveLocation({ bookingId: trip?._id, active: sharingLocation });

  if (loading) {
    return <div className="pp-state" role="status">Fuvar betöltése…</div>;
  }

  if (!trip) {
    return (
      <>
        <div className="pp-topbar">
          <button type="button" className="pp-topbar__back" aria-label="Vissza" onClick={backToDay}>
            <ChevronLeftIcon size={22} width={2.5} />
          </button>
        </div>
        <div className="pp-error" role="alert">{error || 'Ez a fuvar nem található.'}</div>
      </>
    );
  }

  // Who the office will see the message from. Read from the session rather
  // than the trip: the payload carries the CUSTOMER's name, never the
  // driver's own.
  const driverName = principalName(getPortalPrincipal());
  const step = progressIndex(trip);
  const nextEvent = nextTripEvent(trip);
  // Closed by the office before the driver finished it. A trip dropped off
  // (or a no-show) and then Completed is simply finished.
  const closedByOffice = isTripClosed(trip) && !isDroppedOff(trip) && !isNoShow(trip);
  const target = navigationTarget(trip, now);
  const navigation = navigationUrl(target.point);
  const signName = trip.customer_name || passengerManifest(trip)[0]?.name || '';
  const signMeta = [trip.flight_number, place(trip.from).title].filter(Boolean).join(' · ');
  const people = totalPassengers(trip);

  return (
    <>
      <div className="pp-topbar">
        <button type="button" className="pp-topbar__back" aria-label="Vissza" onClick={backToDay}>
          <ChevronLeftIcon size={22} width={2.5} />
        </button>
        <div className="pp-topbar__body">
          <div className="pp-topbar__title">
            {`${formatTime(trip.date_datetime)} · ${dayLabel(trip.date_datetime)}`}
          </div>
          {trip.booking_reference ? (
            <div className="pp-topbar__ref">{trip.booking_reference}</div>
          ) : null}
        </div>
        {trip.status ? (
          <span className="pp-badge pp-badge--ok">{statusLabel(trip.status)}</span>
        ) : null}
      </div>

      <div className="pp-stack">
        <PortalRouteMap trip={trip} target={target} />
        <RouteOrProgramme trip={trip}>
          <TripActions href={navigation} onNameSign={() => setSignOpen(true)} />
        </RouteOrProgramme>

        {/*
          The chat opens with a sentence that fits where the trip actually
          is — and in ENGLISH, which is a decision with a measurement
          behind it (passengerMessage). Everything else the driver reads on
          this screen stays Hungarian; this one is read by the passenger.
        */}
        <CustomerCard
          trip={trip}
          onCall={() => recordContact('call')}
          onWhatsApp={() => recordContact('whatsapp')}
          waMessage={passengerMessage({
            trip,
            driverName,
            nameSign: trip.name_sign && trip.name_sign.kind !== 'none' ? signName : '',
            now,
          })}
        />

        <Note label="Diszpécser üzenete" body={trip.operator_comments} />

        <Manifest trip={trip} />

        <Facts
          rows={[
            { key: 'Utasok', value: passengerSummary(trip) || (people ? `${people} fő` : '') },
            { key: 'Kategória', value: trip.category },
            // No 'Járat' row: FlightCard above carries the number, and with
            // it the one thing the number alone could never say — whether
            // the plane is late.
            { key: 'Autó', value: vehicleLabel(trip) },
            { key: 'Dátum', value: longDate(trip.date_datetime) },
            { key: 'Esemény', value: trip.event_name },
            // Only for an operator that turned "Show Driver the Fee" on; for
            // everyone else the API sends no amount and this row is absent
            // (Facts drops empty rows).
            {
              key: 'Fuvardíj',
              value: trip.operator_fee === undefined || trip.operator_fee === null
                ? ''
                : formatAmount(trip.operator_fee, trip.operator_fee_currency),
            },
          ]}
        />

        {/* Stale only where a beat was actually running: on a trip the API
            would not look the flight up for, a failed read says nothing
            about the arrival. */}
        <FlightCard trip={trip} stale={polls && flightDataStale(refresh)} />

        <OvertimeCard trip={trip} />

        <RecordExpense bookingId={trip._id} tripLabel={trip.booking_reference} />
      </div>

      <div className="pp-foot">
        {/*
          Only while the driver is actually standing at an airport pickup
          waiting for someone. That is the one moment the photo can be
          taken, and the only trips where a no-show turns on whether the
          driver was visibly there with a sign.
        */}
        {isWaitingAtPickup(trip) && isAirportPickupTrip(trip) && !isTripClosed(trip) ? (
          <NameSignAsk
            bookingId={trip._id}
            photo={signPhoto}
            onPhoto={setSignPhoto}
            signName={signName}
            answer={trip.name_sign}
            onAnswer={answerNameSign}
          />
        ) : null}
        <WaitingPanel
          trip={trip}
          busy={busy}
          closed={isTripClosed(trip)}
          driverName={driverName}
          onStartOvertime={(stage) => sendEvent('overtime_started', undefined, { stage })}
          onEndOvertime={() => sendEvent('overtime_ended')}
          onNoShow={openNoShow}
        />
        {/*
          SAID WHILE IT IS HAPPENING. Position sharing is on for every driver
          and there is no switch, so this line is the whole of the
          transparency: a driver must not discover afterwards that their phone
          had been reporting. It names who sees the position and says the
          sharing ends at the drop-off, and it offers NOTHING — no toggle, no
          dismiss. A control that looked like one and did nothing would be
          worse than none.
        */}
        {sharingLocation ? (
          <div className="pp-live-loc" role="status">
            <span className="pp-live-loc__dot" aria-hidden="true" />
            <div className="pp-live-loc__body">
              <div className="pp-live-loc__text">{LIVE_LOCATION_COPY.live}</div>
              <div className="pp-live-loc__detail">{LIVE_LOCATION_COPY.liveDetail}</div>
            </div>
          </div>
        ) : null}
        {isNoShow(trip) ? null : <ProgressStrip index={step} />}
        {closedByOffice ? (
          <div className="pp-closed" role="status">
            <div className="pp-closed__title">Az iroda lezárta ezt a fuvart.</div>
            <div className="pp-closed__detail">
              Ezzel a fuvarral már nincs több teendőd.
            </div>
          </div>
        ) : null}
        {nextEvent ? (
          <button
            type="button"
            className="pp-btn pp-btn--dark pp-btn--block pp-btn--tall"
            onClick={() => sendEvent(nextEvent)}
            disabled={Boolean(busy)}
            aria-busy={busy === nextEvent}
          >
            {busy === nextEvent ? 'Küldés…' : ADVANCE_LABELS[step]}
          </button>
        ) : (
          <>
            {canReportOvertime(trip) ? (
              <button
                type="button"
                className="pp-btn pp-btn--outline pp-btn--block pp-btn--tall"
                onClick={openReport}
                disabled={Boolean(busy)}
              >
                Túlóra jelentése
              </button>
            ) : null}
            {closedByOffice ? null : (
              <div className="pp-stub-note">
                {isNoShow(trip) ? 'Az utas nem jelent meg — rögzítve. ' : ''}
                Ez a fuvar le van zárva.
              </div>
            )}
          </>
        )}
        {eventError && !reportSheet && !noShowOpen ? <div className="pp-form__error" role="alert">{eventError}</div> : null}
      </div>

      {reportSheet ? (
        <OvertimeReportSheet
          title={reportSheet === 'prompt' ? 'Volt túlóra?' : 'Túlóra jelentése'}
          dismissLabel={reportSheet === 'prompt' ? 'Nem volt' : 'Mégse'}
          busy={busy === 'overtime_reported'}
          error={eventError}
          onSubmit={submitReport}
          onDismiss={closeReport}
        />
      ) : null}

      {noShowOpen ? (
        <NoShowSheet
          trip={trip}
          busy={busy === 'no_show'}
          error={eventError}
          position={noShowFix}
          signPhoto={signPhoto}
          driverName={driverName}
          onConfirm={confirmNoShow}
          onCancel={closeNoShow}
        />
      ) : null}

      {signOpen ? (
        <NameSign
          name={signName}
          meta={signMeta}
          branded={brandingAllowed(trip)}
          onClose={closeSign}
        />
      ) : null}

      <Toast message={toast} onDone={clearToast} />
    </>
  );
}
